import { listLikesByComments } from "@/features/comunicacao/api/commentLikesApi";
import { createComment as createAppComment } from "@/features/comunicacao/api/commentsApi";
import { ensureThread as ensureThreadRpc } from "@/features/comunicacao/api/commentThreadsApi";
import {
  appCommentsService,
  type AppCommentThread,
  type RecursoTipo,
} from "@/features/comunicacao/services/appCommentsService";
import { supabase } from "@/features/sistema/services/supabaseClient";

export type ComentarioTipo = "texto" | "foto" | "arquivo";

type UsuarioJoinRow = { id: string; nome: string | null; email: string | null };

function resolveUsuarioJoin(input?: UsuarioJoinRow | UsuarioJoinRow[] | null) {
  if (Array.isArray(input)) {
    return input[0] ?? null;
  }
  return input ?? null;
}

export type ComentarioRow = {
  id: string;
  thread_id: string;
  conteudo: string;
  tipo: ComentarioTipo;
  arquivo_url?: string | null;
  arquivo_nome?: string | null;
  mencoes?: string[] | null;
  usuarios_mencionados?: Array<{ id: string; nome: string; email: string | null }>;
  created_at: string;
  updated_at?: string | null;
  deleted_at?: string | null;
  created_by: string | null;
  criado_por?: { id: string; nome: string; email: string | null };
  curtidas_count?: number;
  curtido_por_mim?: boolean;
};

type FetchOptions = {
  currentUsuarioId?: string | null;
};

const RECURSO: RecursoTipo = "diario_relatorio";

async function ensureDiarioThread(
  obraId: string,
  relatorioId: string,
  opts: { userId?: string | null } = {},
): Promise<AppCommentThread> {
  let thread = await appCommentsService.getThreadByResource(RECURSO, relatorioId);
  if (thread) return thread;

  const ensuredId = await ensureThreadRpc({
    obraId,
    recurso: RECURSO,
    recursoId: relatorioId,
    userId: opts.userId ?? null,
  });

  if (!ensuredId) {
    throw new Error(`Não foi possível garantir thread para o relatório ${relatorioId}`);
  }

  thread = await appCommentsService.getThreadByResource(RECURSO, relatorioId);
  if (!thread) {
    throw new Error(`Thread criada não encontrada para o relatório ${relatorioId}`);
  }

  return thread;
}

async function fetchCommentsByThread(threadId: string, opts: FetchOptions = {}): Promise<ComentarioRow[]> {
  const { data, error } = await supabase
    .from("app_comments")
    .select(
      `
        id,
        thread_id,
        conteudo,
        tipo,
        arquivo_url,
        arquivo_nome,
        mencoes,
        created_at,
        updated_at,
        deleted_at,
        created_by,
        criado_por:usuarios!app_comments_created_by_fkey ( id, nome, email )
      `,
    )
    .eq("thread_id", threadId)
    .is("deleted_at", null)
    .order("created_at", { ascending: true });

  if (error) throw error;

  const rows = (data ?? []) as unknown as Array<{
    id: string;
    thread_id: string;
    conteudo: string | null;
    tipo: ComentarioTipo | null;
    arquivo_url: string | null;
    arquivo_nome: string | null;
    mencoes: string[] | null;
    created_at: string;
    updated_at: string | null;
    deleted_at: string | null;
    created_by: string | null;
    criado_por?: UsuarioJoinRow | UsuarioJoinRow[] | null;
  }>;

  const commentIds = rows.map((row) => row.id);
  const likesById = new Map<string, number>();
  const likedByMe = new Set<string>();

  if (commentIds.length > 0) {
    const likes = await listLikesByComments(commentIds);
    likes.forEach((like) => {
      likesById.set(like.comment_id, (likesById.get(like.comment_id) ?? 0) + 1);
      if (opts.currentUsuarioId && like.usuario_id === opts.currentUsuarioId) {
        likedByMe.add(like.comment_id);
      }
    });
  }

  const mentionIds = new Set<string>();
  rows.forEach((row) => {
    (row.mencoes ?? []).forEach((id) => {
      if (typeof id === "string" && id.trim()) {
        mentionIds.add(id);
      }
    });
  });

  const mentionMap = new Map<string, { id: string; nome: string; email: string | null }>();
  if (mentionIds.size > 0) {
    const { data: mentionUsers, error: mentionError } = await supabase
      .from("usuarios")
      .select("id, nome, email")
      .in("id", Array.from(mentionIds));
    if (!mentionError) {
      (mentionUsers ?? []).forEach((usuario: any) => {
        mentionMap.set(usuario.id, {
          id: usuario.id,
          nome: usuario.nome ?? "Usuário",
          email: usuario.email ?? null,
        });
      });
    }
  }

  return rows.map((row) => {
    const mencoes = Array.isArray(row.mencoes) ? row.mencoes.filter((id) => typeof id === "string" && id.trim()) : null;
    const usuariosMencionados = mencoes
      ? mencoes
          .map((id) => mentionMap.get(id))
          .filter((value): value is { id: string; nome: string; email: string | null } => !!value)
      : [];

    const autor = resolveUsuarioJoin(row.criado_por);

    return {
      id: row.id,
      thread_id: row.thread_id,
      conteudo: row.conteudo ?? "",
      tipo: (row.tipo ?? "texto") as ComentarioTipo,
      arquivo_url: row.arquivo_url,
      arquivo_nome: row.arquivo_nome,
      mencoes,
      usuarios_mencionados: usuariosMencionados,
      created_at: row.created_at,
      updated_at: row.updated_at ?? undefined,
      deleted_at: row.deleted_at ?? undefined,
      created_by: row.created_by ?? null,
      criado_por: autor
        ? {
            id: autor.id,
            nome: autor.nome ?? "Usuario",
            email: autor.email ?? null,
          }
        : undefined,
      curtidas_count: likesById.get(row.id) ?? 0,
      curtido_por_mim: opts.currentUsuarioId ? likedByMe.has(row.id) : undefined,
    };
  });
}

export async function ensureThread(
  obraId: string,
  relatorioId: string,
  opts: { userId?: string | null } = {},
): Promise<AppCommentThread> {
  return ensureDiarioThread(obraId, relatorioId, opts);
}

export async function listComments(threadId: string, opts: FetchOptions = {}): Promise<ComentarioRow[]> {
  if (!threadId) return [];
  return fetchCommentsByThread(threadId, opts);
}

export async function addComment(params: {
  threadId?: string | null;
  texto: string;
  mencoes?: string[];
  arquivoUrl?: string | null;
  arquivoNome?: string | null;
  obraId?: string | null;
  relatorioId?: string | null;
  authorUsuarioId?: string | null;
}) {
  const {
    threadId,
    texto,
    mencoes,
    arquivoUrl,
    arquivoNome,
    obraId = null,
    relatorioId = null,
    authorUsuarioId = null,
  } = params;

  if (!threadId && (!obraId || !relatorioId)) {
    throw new Error("addComment requer threadId ou (obraId, relatorioId)");
  }

  await createAppComment({
    threadId: threadId ?? undefined,
    recurso: threadId ? undefined : RECURSO,
    recursoId: threadId ? undefined : relatorioId ?? undefined,
    obraId: threadId ? undefined : obraId ?? undefined,
    conteudo: texto,
    tipo: arquivoUrl ? "arquivo" : "texto",
    arquivoUrl: arquivoUrl ?? null,
    arquivoNome: arquivoNome ?? null,
    mencoes: mencoes?.length ? mencoes : null,
    createdBy: authorUsuarioId,
  });
}

export async function updateComment(
  id: string,
  novoTexto: string,
  opts: { editorUsuarioId?: string | null; mencoes?: string[] } = {},
) {
  const patch: Record<string, unknown> = {
    conteudo: novoTexto,
    editado: true,
    updated_at: new Date().toISOString(),
  };

  if (opts.editorUsuarioId) {
    patch.updated_by = opts.editorUsuarioId;
  }

  if (opts.mencoes) {
    patch.mencoes = opts.mencoes.length ? opts.mencoes : null;
  }

  const { error } = await supabase.from("app_comments").update(patch).eq("id", id);
  if (error) throw error;
}

export async function softDeleteComment(id: string, opts: { deletedBy?: string | null } = {}) {
  const patch: Record<string, unknown> = {
    deleted_at: new Date().toISOString(),
  };

  if (opts.deletedBy) {
    patch.deleted_by = opts.deletedBy;
  }

  const { error } = await supabase.from("app_comments").update(patch).eq("id", id);
  if (error) {
    const msg = String((error as any)?.message || "");
    // Fallback para ambientes sem a coluna deleted_by: tenta de novo apenas com deleted_at
    if (patch.deleted_by && ((error as any)?.code === "PGRST204" || msg.includes("deleted_by"))) {
      const { error: retryError } = await supabase
        .from("app_comments")
        .update({ deleted_at: patch.deleted_at })
        .eq("id", id);
      if (!retryError) return;
      throw retryError;
    }
    throw error;
  }
}
